// --------------------------------------------------------------
// Created On: 2025-10-22
// Author: Marco Serrano
//
// Last Modified: 2026-09-14
// Modified By: Dimitra Weinstein
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState } from "react";
import AssetManagementWizard from "../AssetManagementWizard/AssetManagementWizard";
import { GetDeviceMapResponseBody } from "src/types/api/GetDeviceMapResponseBody";
import useReduxSelector from "../../hooks/useReduxSelector";
import apiRequest from "../../utilities/api/apiRequest";
import { API } from "../../constants/miscellaneous";
import Spinner from "../Spinner/Spinner";
import SampleDeviceDataButton from "../SampleDeviceDataButton/SampleDeviceDataButton";
import ValidateAssetButton from "../ValidateAssetButton/ValidateAssetButton";
import { UPDATE_ASSETS_PERMISSION } from "src/constants/permissions";
import userHasPermission from "../../utilities/userHasPermission";
import { RECENTLY_COMMUNICATED_ATTRIBUTE_SUPPORTED_DEVICES } from "../../constants/miscellaneous";
import SignalIcon from "../SignalIcon/SignalIcon";
import ValidateAssetModal from "../ValidateAssetButton/ValidateAssetModal/ValidateAssetModal";
import EditAssetButton from "./EditAssetButton/EditAssetButton";
import styles from "./AssetDataButtons.module.scss";

// Buttons for sampling, validating a device, or editing an asset and getting any data needed to use the AssetManagementWizard.
export default function AssetDataButtons(props: Props): Component {
  const currentUser = useReduxSelector((state) => state.currentUser);
  const initialDeviceMap: GetDeviceMapResponseBody = {
    controllers: [],
    deviceTypes: [],
    productManufacturers: [],
    productTypes: [],
    monitoringDevices: [],
    analogSensors: [],
    digitalSensors: [],
    digitalOutputs: [],
    templates: [],
    assetMapIcons: [],
    modbusSensors: [],
    gatewayAssets: [],
    runtimeServiceAttributeMap: {},
  };

  const [deviceMap, setDeviceMap] = useState<GetDeviceMapResponseBody>(initialDeviceMap);
  const [loading, setLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showValidationModal, setShowValidationModal] = useState<boolean>(false);

  // Calls endpoint to retrieve the asset management settings that will populate the wizard.
  async function getAssetManagementSettings(): Promise<void> {
    setLoading(true);
    const [response, responseBody] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/device`,
      "GET",
      null
    )) as [Response, GetDeviceMapResponseBody];

    if (response.ok && responseBody) {
      setDeviceMap(responseBody);
    }
    setLoading(false);
  }

  // Handles the edit asset button click by fetching the asset management settings and showing the modal.
  async function handleEditAsset(): Promise<void> {
    await getAssetManagementSettings();
    setLoading(false);
    setShowModal(true);
  }

  // Closes the AssetManagementWizard modal and then does any additional behavior the parent component defines
  function onClose(): void {
    setShowModal(false);
    props.onClose();
  }

  return (
    <div>
      {props.deviceType !== null && RECENTLY_COMMUNICATED_ATTRIBUTE_SUPPORTED_DEVICES.includes(props.deviceType) && (
        <SignalIcon allAttributes={props.allAttributes} />
      )}
      <span className={styles.titleBarValidationButtons}>
        {props.deviceCanSample && props.allowDeviceSample && (
          <SampleDeviceDataButton
            assetId={props.assetId || 0}
            className="py-auto mx-1"
            buttonName="Sample"
            smallButton={false}
          />
        )}
        {props.deviceCanValidate && props.allowAssetValidate && (
          <ValidateAssetButton
            assetId={props.assetId || null}
            deviceId={props.deviceId || null}
            deviceType={props.deviceType}
            className="py-auto mx-1"
            smallButton={false}
            onShowValidationModal={() => setShowValidationModal(true)}
          />
        )}
      </span>
      {userHasPermission([[UPDATE_ASSETS_PERMISSION]]) && (
        <EditAssetButton className="py-auto mx-1" handleEditAsset={handleEditAsset} />
      )}
      {showValidationModal && (
        <ValidateAssetModal
          assetId={props.assetId}
          deviceId={props.deviceId}
          deviceType={props.deviceType}
          onClose={() => setShowValidationModal(false)}
        />
      )}
      <Spinner loading={loading} />
      <AssetManagementWizard
        assetId={props.assetId}
        showModal={showModal}
        isCreatingNewAsset={false}
        disableDataCollectionSelection={false}
        templates={deviceMap.templates}
        assetMapIcons={deviceMap.assetMapIcons}
        deviceTypes={deviceMap.deviceTypes}
        monitoringDevices={deviceMap.monitoringDevices}
        gatewayAssets={deviceMap.gatewayAssets}
        productTypes={deviceMap.productTypes}
        productManufacturers={deviceMap.productManufacturers}
        controllers={deviceMap.controllers}
        modbusSensors={deviceMap.modbusSensors}
        analogSensors={deviceMap.analogSensors}
        digitalSensors={deviceMap.digitalSensors}
        runtimeServiceAttributeMap={deviceMap.runtimeServiceAttributeMap}
        onClose={onClose}
        onAction={() => {
          /* Do nothing. */
        }}
        hideDelete={true}
      />
    </div>
  );
}

interface Props {
  assetId: number;
  deviceId: number | null;
  deviceType: string | null;
  allAttributes: Attribute[];
  networkAssets: NetworkAsset[] | undefined;
  deviceCanValidate: boolean;
  deviceCanSample: boolean;
  allowAssetValidate: boolean;
  allowDeviceSample: boolean;
  onClose: () => void;
}

interface NetworkAsset {
  assetId: number;
  assetName: string;
  isGateway: boolean;
  networkDeviceTypeModel: string;
  networkDeviceTypeName: string;
  networkDeviceIdentifier: string;
  userHasAccess: boolean;
}

interface Attribute {
  regAttributeId: number;
  attributeCode: string;
  attributeName: string;
  connectedRegAttributeId: number | null;
  unitShortName: string | null;
  unitLongName: string | null;
  icon: string;
  currentValue: string | null;
  currentValueUtc: string | null;
  isHistorical: boolean;
  isGraphable: boolean;
  isStale: boolean;
}
